/**
 * The typed client for the operations layer (services/ops — see Tasks 6/7).
 * Every consequential action goes through `ops.call`; every read goes
 * through one of the named read methods. Nothing here decides anything —
 * the gates live in the ops service (packages/shared's GateCode/GateRefusal,
 * enforced by services/ops/src/gates.ts) and this client only carries their
 * verdict back as a typed `OpsError` so a component can show the layer's own
 * refusal copy instead of inventing one.
 */
import type {
  ConfigField,
  GateCode,
  Line,
  MediaRef,
  OperationArgs,
  OperationName,
  StaffRole,
  ThreadDetail,
  ThreadSummary,
} from '@communicator/shared'
import { GATE_CODES } from '@communicator/shared'
import { loadRuntimeConfig } from '../runtime-config'
import { withSessionRefresh } from './sessionRefresh'

/**
 * Thrown by `ops.call` (and the read methods, for symmetry) on a refused or
 * failed request.
 *
 * `code` is widened beyond `GateCode` on purpose. The operations layer also
 * returns typed non-gate refusals — `otp-unavailable`, `signature-invalid`,
 * `not-personal` — and every one of them carries a message written for a human.
 * While only gate codes produced an OpsError, those messages were discarded and
 * the UI showed "Something went wrong. Try again." instead. That happened with
 * a 503 whose body said, precisely, that no OTP service is wired — the one
 * sentence that would have explained the failure.
 *
 * `isGateCode` still exists for callers that need to branch on a known gate.
 */
export class OpsError extends Error {
  readonly code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'OpsError'
    this.code = code
  }
}

const GATE_CODE_SET: ReadonlySet<string> = new Set(GATE_CODES)

function isGateCode(value: unknown): value is GateCode {
  return typeof value === 'string' && GATE_CODE_SET.has(value)
}

export type QueueFilter = 'queue' | 'all' | 'mine'

export type SearchScope = 'everything' | 'closed' | 'live' | 'chat' | 'sms' | 'calls'

export type SearchGroupType =
  | 'company'
  | 'person'
  | 'ticket'
  | 'tech'
  | 'live-comm'
  | 'closed-comm'
  | 'message'
  | 'close-note'

export interface SearchResultItem {
  id: string
  label: string
  snippet: string
  threadId?: string
}

export interface SearchGroup {
  type: SearchGroupType
  items: SearchResultItem[]
}

export interface SearchResult {
  groups: SearchGroup[]
  total: number
}

export interface Peer {
  id: string
  name: string
  role: StaffRole
  covering: boolean
  typingThreadId: string | null
  readingThreadId: string | null
}

export interface PresenceResult {
  peers: Peer[]
}

/**
 * The staff presence beacon (task 35) — POST /ops/presence/beacon,
 * services/ops's `registerPresenceBeacon`. `threadId: null` is the explicit
 * "I left this thread" signal (deselecting, or navigating away entirely),
 * which the server clears immediately rather than waiting out a TTL — see
 * services/ops/src/presence.ts's `reportPresenceBeacon` doc comment.
 *
 * Deliberately fire-and-forget from every caller in this app: a dropped
 * beacon report is exactly the kind of thing Andrew's own "assume the
 * connection blips" standing note describes, and a missed presence report
 * is never something a dispatcher should see an error banner over — the
 * worst case is simply that a badge doesn't light up (or clears a beat
 * late), never a broken conversation. Callers swallow the rejection; this
 * method still throws `OpsError`/`Error` like every other read/write here,
 * so a caller that *does* want to observe failures (a future retry policy,
 * a test) still can.
 */
async function presenceBeaconRequest(
  doFetch: typeof fetch,
  resolveBase: () => Promise<string>,
  threadId: string | null,
  typing: boolean,
): Promise<void> {
  const base = await resolveBase()
  const res = await doFetch(`${base}/ops/presence/beacon`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ threadId, typing }),
  })
  if (!res.ok) throw await refusalFor(res)
}

/**
 * "I am at this machine" — Slack's active/away, reported app-wide.
 *
 * Separate from the thread beacon above because it has to keep reporting when
 * no conversation is open at all. Same best-effort contract: a missed report
 * costs a badge, never a conversation.
 */
async function presenceActivityRequest(
  doFetch: typeof fetch,
  resolveBase: () => Promise<string>,
  active: boolean,
): Promise<void> {
  const base = await resolveBase()
  const res = await doFetch(`${base}/ops/presence/activity`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ active }),
  })
  if (!res.ok) throw await refusalFor(res)
}

export interface SelfUpdateResult {
  /** The caller's own coverage flag, exactly as the server now holds it — the one value CoverageToggle's switch may ever show. */
  covering: boolean
  /** The caller's own role, exactly as the server now holds it. Still returned by the wire response even though nothing here writes it any more — see `setCoverage`'s own note. */
  role: StaffRole
  /**
   * "Is anyone covering at all" — ANY role, dispatch or tech, matching
   * routes/client-state.ts's readCoverage exactly. See
   * services/ops/src/routes/coverage.ts's header comment for why a covering
   * tech counts the same as a covering dispatcher: a real, reachable person
   * is a real, reachable person, and the console must never tell staff
   * "Not covering" while a client is correctly being told otherwise.
   */
  anyoneCovering: boolean
}

interface SelfUpdateBody {
  covering?: boolean
}

/**
 * Task 38: the console's own coverage flip, finally connected to the
 * server. `CoverageToggle` used to write straight into the Zustand store and
 * nowhere else — the switch could show a value the `staff` table, and
 * therefore `routes/client-state.ts`'s client-facing "someone's here"
 * signal, never heard about. This is the missing round trip: POST
 * /ops/coverage (services/ops/src/routes/coverage.ts — the route still
 * lives at that path/name because services/ops/src/server.ts already
 * imports `registerCoverage` from it). Deliberately NOT fire-and-forget like
 * `presenceBeacon` above — a dropped report here is exactly the "the switch
 * says one thing, the database says another" bug this whole line of work
 * exists to close, so the caller must be able to observe and react to a
 * failure, never swallow it.
 *
 * USED TO ALSO CARRY `role` (a task-38-follow-up `setRole`, for the
 * now-deleted `RoleSegment`). Users-and-permissions design §1 closed the
 * hole that self-service role change actually was — `dispatch` is exactly
 * what grants reach-across-lanes in gates.ts — so `POST /ops/coverage` now
 * refuses a `role` key outright (`role-not-settable`); a client-side
 * `setRole` that only ever produces a guaranteed 400 has nothing left to do
 * here, so it was removed along with the component that called it. Role is
 * admin-assigned now, through `ops.updateUser` below (`PATCH
 * /ops/users/:id`).
 */
async function updateSelfRequest(
  doFetch: typeof fetch,
  resolveBase: () => Promise<string>,
  body: SelfUpdateBody,
): Promise<SelfUpdateResult> {
  const base = await resolveBase()
  const res = await doFetch(`${base}/ops/coverage`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await refusalFor(res)
  return (await res.json()) as SelfUpdateResult
}

/**
 * One row of the admin-only staff roster (users-and-permissions design §6,
 * `GET /ops/users` — services/ops/src/routes/users.ts's `UserOut`). Kept as
 * its own named type (rather than an inline shape on the two functions
 * below) because `components/settings/UsersSection.tsx` and
 * `lib/userPermissions.ts` both need to name it.
 */
/**
 * One Halo agent, as the picker shows it.
 *
 * `name` is the value stored and sent as `who` — never reconstructed from a
 * person's real name. Halo's `name` is free-form: on this instance two agents
 * are their email addresses and two are bare first names.
 */
export interface HaloAgent {
  id: string
  name: string
}

export interface StaffUser {
  id: string
  email: string
  name: string
  role: StaffRole
  admin: boolean
  superAdmin: boolean
  active: boolean
  covering: boolean
  /** The 3CX extension this person sits at — admin-set, null when unset. */
  ext: string | null
  /**
   * The Halo agent DISPLAY NAME this person's ticket writes are attributed to —
   * admin-set, null when unset. Null means writes land under the API app's own
   * agent, which is how this system behaved before the field existed.
   */
  haloAgentName: string | null
  createdAt: string
}

/** POST /ops/lines body (Task 3, services/ops/src/routes/lines.ts). */
interface CreateLineBody {
  number: string
  label: string
  autoClaimStaffId?: string | null
  isPrimary?: boolean
}

/**
 * PATCH /ops/lines/:id body. `number` is deliberately absent — the route
 * refuses its mere presence outright (`number-not-settable`), and `isPrimary`
 * is typed `true` only: a patch can promote a line, never demote one
 * directly (services/ops/src/routes/lines.ts's own comment on why).
 */
interface UpdateLineBody {
  label?: string
  autoClaimStaffId?: string | null
  isPrimary?: true
}

interface UpdateStaffUserBody {
  role?: StaffRole
  admin?: boolean
  active?: boolean
  /** `null` clears it — someone who moves desks must be removable. */
  ext?: string | null
  /**
   * The Halo agent DISPLAY NAME to attribute this person's ticket writes to.
   * `null` clears it, which returns them to the pre-field behaviour: writes
   * land under the API app's own agent rather than on anyone's timesheet.
   */
  haloAgentName?: string | null
}

/**
 * One of a staff member's signed-in sessions (Task 3, spec 2026-09-11 — the
 * long-session bargain's other half), as `GET /ops/users/:id/sessions`
 * returns it (services/ops/src/routes/user-sessions.ts's `SessionOut`).
 * Client-local: the wire shape is the contract, so there is no
 * `@communicator/shared` type for this.
 */
export interface StaffSession {
  id: string
  createdAt: string
  /** Null for a session that has never rotated. */
  lastRotatedAt: string | null
  expiresAt: string
  /** Null when the sign-in never recorded one. Display-only — see UserSessions.tsx's `uaLabel`. */
  userAgent: string | null
}

/**
 * What Halo alone resolves a handle to RIGHT NOW, independent of any pinned
 * alias — `GET /ops/aliases`'s per-row `live` (Task 2,
 * services/ops/src/routes/aliases.ts's `resolveHandleViaHalo`, which
 * deliberately skips the alias table). `unavailable` means this deployment
 * had no reachable Halo client for the request, not that Halo said "no
 * match" — that is `none`, a real, resolved answer.
 */
export type AliasLive =
  | { kind: 'match'; contactId: string; name: string | null; clientId: string | null; clientName: string | null }
  | { kind: 'ambiguous'; clientId: string | null; clientName: string | null; count: number }
  | { kind: 'none' }
  | { kind: 'unavailable' }

/**
 * One remembered phone→contact pin, beside what Halo alone would resolve
 * that number to right now (`GET /ops/aliases`'s `AliasRowOut`). Client-local,
 * same reasoning as `StaffSession` above: the wire shape is the contract, so
 * there is no `@communicator/shared` type for this.
 */
export interface AliasRow {
  handle: string
  pinnedContactId: string
  pinnedName: string | null
  pinnedClientId: string | null
  pinnedClientName: string | null
  live: AliasLive
  /** `true` unless `live` is the exact same contact the alias pins — `unavailable` counts as unknown, never a mismatch. */
  mismatch: boolean
}

/** What the deployment's environment actually holds for an env-backed field. */
export interface EnvBackedField {
  envVar: string
  set: boolean
  /** Present for non-secrets only. A secret reports presence and nothing more. */
  value?: string
}

/** One hop of a call. Mirrors what /ops/threads/:id/call-legs returns. */
export interface CallLeg {
  from: string
  to: string
  /** The hop as a person would read it — a name where we have one. */
  fromLabel: string
  toLabel: string
  talkS: number
  ringS: number | null
  answered: boolean
  /** Who ended this leg, already resolved against the call's direction. */
  endedBy: 'caller' | 'us' | null
  transferred: boolean
}

export interface CallLegsResult {
  legs: CallLeg[]
  /** Talk time summed across legs — the call as the customer experienced it. */
  totalTalkS: number
}

export interface ConfigResult {
  fields: ConfigField[]
  values: Record<string, unknown>
  /**
   * Keyed by field id, for fields whose real value lives in the environment
   * rather than the config table. Absent on an older ops build, which readers
   * must treat as "unknown", never as "not set".
   */
  env?: Record<string, EnvBackedField>
}

interface OpsClientDeps {
  /** Defaults to the global `fetch`. Override in tests. */
  fetchImpl?: typeof fetch
  /**
   * Skip the 401-refresh-retry wrapper. For tests that assert raw 401
   * behaviour; production never sets it.
   */
  skipSessionRefresh?: boolean
  /** Called when a refresh failed and the person is genuinely signed out. */
  onSignedOut?: () => void
  /**
   * Defaults to reading /runtime-config.json via loadRuntimeConfig(). Pass a
   * literal string (or a function returning one) in tests to skip that fetch
   * entirely.
   */
  baseUrl?: string | (() => string | Promise<string>)
}

async function readJsonBody(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

async function refusalFor(res: Response): Promise<OpsError | Error> {
  const body = (await readJsonBody(res)) as { error?: { code?: unknown; message?: unknown } } | null
  const code = body?.error?.code
  const message =
    typeof body?.error?.message === 'string' ? body.error.message : `Request failed with status ${res.status}`
  // Any structured `{ error: { code, message } }` is a typed refusal the
  // service chose to explain. Surface it. Only a body with no code at all —
  // a proxy error page, a crash — degrades to a bare Error.
  return typeof code === 'string' && code.length > 0 ? new OpsError(code, message) : new Error(message)
}

/**
 * Builds an ops client. `apps/console` uses the default singleton `ops`
 * below; this factory exists so tests (and any future caller that needs a
 * different origin, e.g. an AI-operator MCP wrapper per the design doc §7)
 * can inject their own fetch and base URL.
 */
export function createOpsClient(deps: OpsClientDeps = {}) {
  const rawFetch: typeof fetch = deps.fetchImpl ?? ((...args) => fetch(...args))

  async function resolveBase(): Promise<string> {
    if (deps.baseUrl !== undefined) {
      return typeof deps.baseUrl === 'function' ? deps.baseUrl() : deps.baseUrl
    }
    return (await loadRuntimeConfig()).apiBase
  }

  // Wrapped once, here, so every method below inherits it — including any added
  // later. Wrapping per-method is how you end up with one that quietly does not
  // refresh, and the symptom (an 8h sign-out on one screen only) would be
  // miserable to track down.
  const doFetch: typeof fetch = deps.skipSessionRefresh
    ? rawFetch
    : withSessionRefresh(rawFetch, {
        fetchImpl: rawFetch,
        resolveBase,
        onSignedOut: deps.onSignedOut,
      })

  async function call<K extends OperationName>(op: K, args: OperationArgs[K]): Promise<unknown> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/${op}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(args),
    })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { ok?: boolean; data?: unknown } | null
    return body?.data
  }

  async function threads(filter: QueueFilter, tech?: string): Promise<ThreadSummary[]> {
    const base = await resolveBase()
    const qs = new URLSearchParams({ filter })
    if (tech) qs.set('tech', tech)
    const res = await doFetch(`${base}/ops/threads?${qs.toString()}`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { threads?: ThreadSummary[] } | null
    return body?.threads ?? []
  }

  async function thread(id: string): Promise<ThreadDetail> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/threads/${encodeURIComponent(id)}`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    return (await readJsonBody(res)) as ThreadDetail
  }

  async function search(q: string, scope: SearchScope = 'everything'): Promise<SearchResult> {
    const base = await resolveBase()
    const qs = new URLSearchParams({ q, scope })
    const res = await doFetch(`${base}/ops/search?${qs.toString()}`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    return (await readJsonBody(res)) as SearchResult
  }

  async function config(): Promise<ConfigResult> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/config`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    return (await readJsonBody(res)) as ConfigResult
  }

  /** This person's own preferences. Scoped to the caller by the service — no id is sent. */
  async function preferences(): Promise<{ values: Record<string, unknown> }> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/preferences`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    return (await readJsonBody(res)) as { values: Record<string, unknown> }
  }

  async function setPreference(id: string, value: unknown): Promise<void> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/preferences`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id, value }),
    })
    if (!res.ok) throw await refusalFor(res)
  }

  /** Every leg of a voice thread's call, in order, with who hung up. */
  async function callLegs(threadId: string): Promise<CallLegsResult> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/threads/${encodeURIComponent(threadId)}/call-legs`, {
      credentials: 'include',
    })
    if (!res.ok) throw await refusalFor(res)
    return (await readJsonBody(res)) as CallLegsResult
  }

  async function presence(): Promise<PresenceResult> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/presence`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    return (await readJsonBody(res)) as PresenceResult
  }

  async function presenceBeacon(threadId: string | null, typing: boolean): Promise<void> {
    await presenceBeaconRequest(doFetch, resolveBase, threadId, typing)
  }

  async function presenceActivity(active: boolean): Promise<void> {
    await presenceActivityRequest(doFetch, resolveBase, active)
  }

  async function setCoverage(covering: boolean): Promise<SelfUpdateResult> {
    return updateSelfRequest(doFetch, resolveBase, { covering })
  }

  /**
   * The admin-only staff roster (users-and-permissions design §6). Refused
   * with `not-admin` for anyone but an admin — `UsersSection.tsx` only ever
   * calls this once it has already checked `store.admin` itself (moved
   * 2026-08-10 from a route-level `RequireAdmin` gate to that same check
   * inline), but the real refusal (and its reason) comes from the server
   * regardless of what the console gates.
   */
  async function users(): Promise<StaffUser[]> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/users`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { users?: StaffUser[] } | null
    return body?.users ?? []
  }

  /**
   * The Halo agents an admin may attribute a staff member's writes to.
   *
   * `available: false` means Halo could not be reached — NOT that there are no
   * agents. The caller must tell those apart: an empty list rendered as a
   * picker with nothing in it reads as "you have no agents", which would be a
   * lie, so `UsersSection` falls back to a text box in that case.
   */
  async function haloAgents(): Promise<{ agents: HaloAgent[]; available: boolean }> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/halo-agents`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { agents?: HaloAgent[]; available?: boolean } | null
    return { agents: body?.agents ?? [], available: body?.available === true }
  }

  /**
   * Task 8 (media images): uploads one image to `POST /ops/media` — a
   * multipart body carrying the `threadId` field plus the file itself, never
   * JSON like `call`'s other operations (a file can't ride a
   * `content-type: application/json` body). No `content-type` header is set
   * here on purpose — the browser's own `fetch`/`FormData` picks the
   * multipart boundary and sets the header itself; overriding it strips that
   * boundary and the server can no longer parse the parts.
   *
   * Returns the stored `MediaRef` ({id, mime}) the `send` operation's
   * `mediaIds` later references — this call only stores bytes against a
   * thread, it does not itself attach anything to a message. Refused the
   * same way every other method here is: a non-2xx becomes `OpsError`/`Error`
   * via `refusalFor`, carrying the service's own `bad-thread` / `too-large` /
   * `not-an-image` explanation for the composer's error strip to show
   * verbatim rather than inventing copy.
   */
  async function uploadMedia(threadId: string, file: File): Promise<MediaRef> {
    const base = await resolveBase()
    const form = new FormData()
    form.append('threadId', threadId)
    form.append('file', file)
    const res = await doFetch(`${base}/ops/media`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { media?: MediaRef } | null
    if (!body?.media) throw new Error('Malformed response from POST /ops/media.')
    return body.media
  }

  /**
   * Task 8: the authed blob fetch behind `useMediaBlob` — `GET
   * /ops/media/:id` (staff session only, per services/ops/src/routes/media.ts's
   * header comment) returns raw bytes with the stored `content-type`, which
   * is exactly what `Response#blob()` needs to hand back a `Blob` a caller
   * can feed straight to `URL.createObjectURL`. This method itself creates no
   * object URL and revokes nothing — that lifecycle (create on load, revoke
   * on unmount/id-change) belongs to the hook, not this thin transport
   * wrapper, so every caller gets the same revoke discipline instead of each
   * reinventing it.
   */
  async function mediaBlob(id: string): Promise<Blob> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/media/${encodeURIComponent(id)}`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    return await res.blob()
  }

  /**
   * One staff-row change — role, admin and/or active (users-and-permissions
   * design §3/§6, `PATCH /ops/users/:id`). Returns the target's real,
   * persisted-and-read-back row, never an echo of what was requested — the
   * same discipline `setCoverage` follows for the caller's own row, and
   * exactly what `UsersSection.tsx` needs so a row only ever shows what the
   * server actually holds, never an optimistic guess a refusal would have to
   * unwind.
   */
  async function updateUser(id: string, patch: UpdateStaffUserBody): Promise<StaffUser> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { user?: StaffUser } | null
    if (!body?.user) throw new Error('Malformed response from PATCH /ops/users/:id.')
    return body.user
  }

  /**
   * The phone numbers this deployment answers on (Task 3/6, spec 2026-09-05
   * §Settings, `GET /ops/lines`). Any signed-in staff actor may read this —
   * it is not admin-gated server-side, unlike the three methods below — but
   * `LinesSection.tsx` only calls it once it has already checked
   * `store.admin` itself, the same self-gate `UsersSection.tsx` uses for
   * `ops.users()`, because this pane also drives the auto-claim picker from
   * that admin-only endpoint.
   */
  async function lines(): Promise<Line[]> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/lines`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { lines?: Line[] } | null
    return body?.lines ?? []
  }

  /** `POST /ops/lines` — admin only server-side; returns the created row (201). */
  async function createLine(input: CreateLineBody): Promise<Line> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/lines`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { line?: Line } | null
    if (!body?.line) throw new Error('Malformed response from POST /ops/lines.')
    return body.line
  }

  /** `PATCH /ops/lines/:id` — admin only server-side; returns the persisted-and-read-back row. */
  async function updateLine(id: string, patch: UpdateLineBody): Promise<Line> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/lines/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { line?: Line } | null
    if (!body?.line) throw new Error('Malformed response from PATCH /ops/lines/:id.')
    return body.line
  }

  /** `DELETE /ops/lines/:id` — admin only server-side; the 409 `primary-in-use` refusal is the real gate, not a client-side pre-check. */
  async function deleteLine(id: string): Promise<void> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/lines/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) throw await refusalFor(res)
  }

  /**
   * A staff member's signed-in sessions (Task 3, over Task 2's HTTP surface —
   * services/ops/src/routes/user-sessions.ts). Admin-only server-side, with
   * no non-admin read path at all (unlike `lines`) — that file's own header
   * comment explains why seeing another staff member's browsers/devices is
   * not something a plain technician has any business reading.
   *
   * `uncertainBefore` is the server's own migration-025 apply time (that
   * route's `getUncertainBefore`), not a literal baked into the console — see
   * `UserSessions.tsx`'s use of it for why a client-side literal drifts from
   * reality the moment the deploy date changes.
   */
  async function userSessions(userId: string): Promise<{ sessions: StaffSession[]; uncertainBefore: string }> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/users/${encodeURIComponent(userId)}/sessions`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { sessions?: StaffSession[]; uncertainBefore?: string } | null
    return { sessions: body?.sessions ?? [], uncertainBefore: body?.uncertainBefore ?? new Date(0).toISOString() }
  }

  /**
   * Revokes one session — `POST /ops/users/:id/sessions/:sessionId/revoke`.
   * No body: both ids are already in the URL. See that route's own header
   * comment for what "revoke" actually means (blocks the next refresh; does
   * not invalidate an access token already live in a browser tab).
   */
  async function revokeSession(userId: string, sessionId: string): Promise<void> {
    const base = await resolveBase()
    const res = await doFetch(
      `${base}/ops/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(sessionId)}/revoke`,
      { method: 'POST', credentials: 'include' },
    )
    if (!res.ok) throw await refusalFor(res)
  }

  /**
   * Revokes every one of a staff member's sessions — `POST
   * /ops/users/:id/sessions/revoke-all`. Returns the SERVER's own count,
   * which can exceed the number of rows a caller's list ever showed: the
   * route also flips expired-but-unrevoked rows the list never displayed in
   * the first place (Task 2 review's carried ruling). Callers must report
   * this number, never the size of their own local list.
   */
  async function revokeAllSessions(userId: string): Promise<number> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/users/${encodeURIComponent(userId)}/sessions/revoke-all`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { revoked?: number } | null
    return body?.revoked ?? 0
  }

  /**
   * Every remembered phone→contact pin, beside what Halo alone would resolve
   * that number to right now — `GET /ops/aliases` (Task 2,
   * services/ops/src/routes/aliases.ts). Admin-only server-side, with no
   * non-admin read path at all — same floor as `userSessions` above; that
   * route's own header comment explains why seeing (and certainly removing)
   * another person's hand-made pin is not something a plain technician has
   * any business reading.
   */
  async function aliases(): Promise<AliasRow[]> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/aliases`, { credentials: 'include' })
    if (!res.ok) throw await refusalFor(res)
    const body = (await readJsonBody(res)) as { aliases?: AliasRow[] } | null
    return body?.aliases ?? []
  }

  /**
   * Removes one remembered alias — `POST /ops/aliases/:handle/remove`. The
   * handle is a phone number, which can carry a leading `+` that is not
   * legal raw in a URL path segment, so it is always encoded here rather
   * than left to a caller. No body: the handle is already in the URL.
   */
  async function removeAlias(handle: string): Promise<void> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/aliases/${encodeURIComponent(handle)}/remove`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) throw await refusalFor(res)
  }

  return {
    call,
    threads,
    thread,
    search,
    config,
    preferences,
    setPreference,
    callLegs,
    presence,
    presenceBeacon,
    presenceActivity,
    setCoverage,
    users,
    updateUser,
    haloAgents,
    uploadMedia,
    mediaBlob,
    lines,
    createLine,
    updateLine,
    deleteLine,
    userSessions,
    revokeSession,
    revokeAllSessions,
    aliases,
    removeAlias,
  }
}

/**
 * The app's client.
 *
 * `onSignedOut` is deliberately NOT wired here: this module must not import the
 * store (the store imports types from here, and a value-level cycle is what
 * produced the TDZ crash a previous round had to chase). bootstrap.ts owns that
 * wiring for the stream, and a failed refresh on a plain request surfaces as
 * the original 401, which every caller already handles as a refusal.
 */
export const ops = createOpsClient()
